import { useState, useEffect, useRef } from 'react';
import { Send, Pill, Heart, Sparkles, Activity, AlertTriangle, ArrowLeftRight, MessageCircle, Zap, CloudRain, Search, Wind, Mic } from 'lucide-react';
import { DOCTORS, MOODS, QUICK_PROMPTS } from '../lib/constants';
import { callChatAPI, getMoodLog } from '../lib/mock';
import VoiceRecorder from '../components/VoiceRecorder';

const ICON_MAP = { MessageCircle, Zap, CloudRain, Search, Pill, Wind };

// AI routing logic based on patient current state
function suggestDoctor(mood, text) {
  const t = (text || '').toLowerCase();
  const medicalKeywords = ['دواء', 'جرعة', 'أدوية', 'نوم', 'طاقة', 'ليثيوم', 'أرق', 'الأعراض'];
  const dynamicKeywords = ['طفولة', 'ذكرى', 'عائلة', 'أفهم', 'جذور', 'ماضي', 'أشعر', 'حزن', 'ألم'];
  if (mood === 'manic' || medicalKeywords.some(k => t.includes(k))) return 'g';
  if (dynamicKeywords.some(k => t.includes(k))) return 'k';
  if (['low', 'bad', 'anxious'].includes(mood)) return 'k';
  if (['manic'].includes(mood)) return 'g';
  return null;
}

export default function ChatScreen({ patient }) {
  const [curDoc, setCurDoc] = useState('k');
  const [curMood, setCurMood] = useState(null);
  const [curMoodLabel, setCurMoodLabel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showVoice, setShowVoice] = useState(false);
  const chatRef = useRef(null);
  const taRef = useRef(null);
  const doc = DOCTORS[curDoc];

  useEffect(() => {
    // Pre-fill mood from latest mood log
    (async () => {
      const log = await getMoodLog(patient.id);
      if (log[0]) {
        const m = MOODS.find(x => x.code === log[0].code);
        if (m) {
          setCurMood(`${m.emoji} ${m.label}`);
          setCurMoodLabel(m.code);
          const s = suggestDoctor(m.code, '');
          if (s && s !== curDoc) setSuggestion(s);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, loading]);

  const applySuggestion = () => { setCurDoc(suggestion); setSuggestion(null); setMessages([]); setHistory([]); setShowWelcome(true); };

  const selectDoc = (d) => {
    if (loading || d === curDoc) return;
    setCurDoc(d); setHistory([]); setMessages([]); setShowWelcome(true); setSuggestion(null);
  };

  const sendMessage = async (overrideText = null) => {
    const text = (overrideText || input).trim();
    if (!text || loading) return;
    setShowWelcome(false);
    setInput('');
    if (taRef.current) taRef.current.style.height = 'auto';

    // Check for smart suggestion
    const s = suggestDoctor(curMoodLabel, text);
    if (s && s !== curDoc && messages.length === 0) {
      setSuggestion(s);
    }

    const userMsg = { id: Date.now(), role: 'user', text, mood: curMood, doctor: curDoc };
    setMessages(prev => [...prev, userMsg]);
    const fullText = curMood ? `[الحالة المزاجية: ${curMood}]\n${text}` : text;
    const newHistory = [...history, { role: 'user', content: fullText }];
    setHistory(newHistory);
    setLoading(true);

    try {
      const resp = await callChatAPI({
        patient_id: patient.id,
        doctor: curDoc,
        mood: curMood,
        mood_label: curMoodLabel,
        text,
        history
      });
      const cleanText = resp.reply || '';
      const meta = resp.meta || {};

      const aiMsg = { id: Date.now() + 1, role: 'ai', text: cleanText, doctor: curDoc, meta, userQuery: text };
      setMessages(prev => [...prev, aiMsg]);
      setHistory([...newHistory, { role: 'assistant', content: cleanText }]);
    } catch (e) {
      const errMsg = { id: Date.now() + 1, role: 'ai', text: 'عذراً، حدث خطأ في الاتصال. حاولي مرة أخرى.', doctor: curDoc, meta: null };
      setMessages(prev => [...prev, errMsg]);
    } finally { setLoading(false); }
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };
  const resizeTa = (el) => { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 120) + 'px'; };

  const suggested = suggestion ? DOCTORS[suggestion] : null;

  return (
    <div className="flex flex-col min-h-full">
      {/* Doctor selector - horizontal pills */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex gap-2">
          {Object.values(DOCTORS).map(d => {
            const isActive = curDoc === d.id;
            const isPurple = d.id === 'k';
            return (
              <button
                key={d.id}
                onClick={() => selectDoc(d.id)}
                className={`flex-1 flex items-center gap-2 rounded-2xl px-3 py-2.5 transition-all duration-300 border-2 ${
                  isActive ? 'shadow-md' : 'hover:scale-[1.02]'
                }`}
                style={{
                  background: isActive ? (isPurple ? 'var(--primary-bg)' : 'var(--secondary-bg)') : 'var(--surface)',
                  borderColor: isActive ? (isPurple ? 'var(--primary)' : 'var(--secondary)') : 'var(--border-soft)'
                }}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-base flex-shrink-0"
                     style={{ background: isPurple ? 'var(--primary-bg)' : 'var(--secondary-bg)', border: `1.5px solid ${isPurple ? 'var(--primary-light)' : 'var(--secondary-light)'}` }}>
                  {d.emoji}
                </div>
                <div className="text-right flex-1 min-w-0">
                  <div className="text-[11.5px] font-extrabold truncate" style={{ color: 'var(--text)' }}>{d.short}</div>
                  <div className="text-[9.5px] truncate" style={{ color: 'var(--text-2)' }}>{d.tagline}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Smart suggestion banner */}
      {suggested && (
        <div className="mx-4 mb-2 flex items-center gap-2 rounded-2xl p-3 animate-slide-down border"
             style={{ background: 'var(--honey-bg)', borderColor: 'var(--honey)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#f0c67440' }}>
            <Sparkles className="w-4 h-4" style={{ color: '#c9871f' }} />
          </div>
          <div className="flex-1 text-[12px] leading-snug" style={{ color: '#8a5f1a' }}>
            <span className="font-bold">اقتراح ذكي:</span> بناءً على حالتك، التحدث مع <span className="font-bold">{suggested.short}</span> قد يكون أفضل
          </div>
          <button onClick={applySuggestion} className="rounded-xl px-3 py-1.5 text-[11px] font-bold text-white flex-shrink-0" style={{ background: 'linear-gradient(135deg, #f0b16c, #d99149)' }}>
            تحويل
          </button>
        </div>
      )}

      {/* Mood pills */}
      <div className="px-4 pb-2 overflow-x-auto flex gap-1.5 items-center" style={{ scrollbarWidth: 'none' }}>
        <span className="text-[10px] font-bold whitespace-nowrap flex-shrink-0" style={{ color: 'var(--text-2)' }}>حالتك:</span>
        {MOODS.map(m => {
          const isSel = curMoodLabel === m.code;
          return (
            <button
              key={m.code}
              onClick={() => { setCurMood(`${m.emoji} ${m.label}`); setCurMoodLabel(m.code); const s = suggestDoctor(m.code, ''); if (s && s !== curDoc) setSuggestion(s); }}
              className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap transition-all border ${isSel ? 'scale-105 shadow-sm' : ''}`}
              style={isSel ? { background: 'var(--primary-bg)', borderColor: 'var(--primary)', color: 'var(--primary-dark)' } : { background: 'var(--surface)', borderColor: 'var(--border-soft)', color: 'var(--text-2)' }}
            >
              {m.emoji} {m.label}
            </button>
          );
        })}
      </div>

      {/* Chat area */}
      <div ref={chatRef} className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
        {showWelcome && messages.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-4 animate-fade-in">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-md"
                 style={{ background: doc.id === 'k' ? 'var(--primary-bg)' : 'var(--secondary-bg)', border: `2px solid ${doc.id === 'k' ? 'var(--primary-light)' : 'var(--secondary-light)'}` }}>
              {doc.emoji}
            </div>
            <div className="text-center">
              <h2 className="text-[17px] font-extrabold mb-1" style={{ color: 'var(--text)' }}>
                {doc.id === 'k' ? `أهلاً ${patient.name}، أنا هنا معك` : `${patient.name}، لنبدأ بتقييم هادئ`}
              </h2>
              <p className="text-[12.5px] leading-relaxed max-w-[280px] mx-auto" style={{ color: 'var(--text-2)' }}>
                {doc.id === 'k' ? 'Kai Jamison — لنستكشف معاً ما تشعرين به بعمق' : 'Goodwin — أسألك أسئلة دقيقة لفهم أعراضك'}
              </p>
            </div>
            <div className="flex flex-col gap-1.5 w-full mt-2">
              {QUICK_PROMPTS.map((q, i) => {
                const Icon = ICON_MAP[q.icon] || MessageCircle;
                return (
                  <button
                    key={i}
                    onClick={() => sendMessage(q.text)}
                    className="card-soft hover:shadow-md active:scale-[0.99] transition-all px-3 py-2.5 text-right flex items-center gap-2.5 group"
                  >
                    <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--primary-bg)' }}>
                      <Icon className="w-3.5 h-3.5" style={{ color: 'var(--primary-dark)' }} />
                    </div>
                    <span className="flex-1 text-[13px] font-semibold" style={{ color: 'var(--text)' }}>{q.text}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {messages.map(msg => {
          if (msg.role === 'user') {
            return (
              <div key={msg.id} className="flex gap-2 items-start flex-row-reverse animate-fade-in">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'var(--peach-bg)', border: '1.5px solid var(--peach)' }}>
                  <Heart className="w-3.5 h-3.5" style={{ color: '#d17c53' }} />
                </div>
                <div className="max-w-[82%] bubble-me px-3.5 py-2.5 text-[13.5px] leading-relaxed shadow-sm"
                     style={{ background: 'linear-gradient(135deg, #fdefe4, #fdedeb)', border: '1px solid var(--peach)', color: 'var(--text)' }}>
                  {msg.text}
                </div>
              </div>
            );
          }
          const md = DOCTORS[msg.doctor];
          const isPurple = md.id === 'k';
          return (
            <div key={msg.id} className="flex gap-2 items-start animate-fade-in">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-sm" style={{ background: isPurple ? 'var(--primary-bg)' : 'var(--secondary-bg)', border: `1.5px solid ${isPurple ? 'var(--primary-light)' : 'var(--secondary-light)'}` }}>
                {md.emoji}
              </div>
              <div className="max-w-[85%] bubble-doc px-3.5 py-2.5 text-[13.5px] leading-relaxed shadow-sm card-soft">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span className="text-[10.5px] font-extrabold" style={{ color: isPurple ? 'var(--primary-dark)' : 'var(--secondary-dark)' }}>{md.short}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold" style={{ background: isPurple ? 'var(--primary-bg)' : 'var(--secondary-bg)', color: isPurple ? 'var(--primary-dark)' : 'var(--secondary-dark)' }}>{md.title}</span>
                </div>
                {msg.meta?.refer && msg.meta.refer !== 'none' && (
                  <div className="rounded-xl px-3 py-2 mb-2 flex items-center gap-2" style={{ background: 'var(--honey-bg)', border: '1px solid var(--honey)' }}>
                    <ArrowLeftRight className="w-3.5 h-3.5" style={{ color: '#c9871f' }} />
                    <span className="text-[11px] font-semibold" style={{ color: '#8a5f1a' }}>إحالة إلى {msg.meta.refer === 'goodwin' ? DOCTORS.g.short : DOCTORS.k.short}</span>
                  </div>
                )}
                <div className="whitespace-pre-wrap" style={{ color: 'var(--text)' }}>{msg.text}</div>
                {msg.meta?.risk > 0 && (
                  <div className="mt-2.5 flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border-soft)' }}>
                    <span className="text-[9.5px] font-semibold" style={{ color: 'var(--text-2)' }}>الانتباه السريري</span>
                    <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-soft)' }}>
                      <div className="h-full rounded-full transition-all duration-500"
                           style={{ width: `${msg.meta.risk * 10}%`, background: msg.meta.risk >= 7 ? 'var(--danger)' : msg.meta.risk >= 4 ? 'var(--warning)' : 'var(--success)' }}></div>
                    </div>
                    <span className="text-[9.5px] font-extrabold" style={{ color: msg.meta.risk >= 7 ? 'var(--danger)' : msg.meta.risk >= 4 ? 'var(--warning)' : 'var(--success)' }}>{msg.meta.risk}/10</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {loading && (
          <div className="flex gap-2 items-start animate-fade-in">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm" style={{ background: doc.id === 'k' ? 'var(--primary-bg)' : 'var(--secondary-bg)', border: `1.5px solid ${doc.id === 'k' ? 'var(--primary-light)' : 'var(--secondary-light)'}` }}>{doc.emoji}</div>
            <div className="bubble-doc card-soft px-3.5 py-3">
              <div className="text-[10.5px] font-extrabold mb-1.5" style={{ color: doc.id === 'k' ? 'var(--primary-dark)' : 'var(--secondary-dark)' }}>{doc.short} يكتب…</div>
              <div className="flex gap-1 items-center py-1">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: doc.id === 'k' ? 'var(--primary)' : 'var(--secondary)', animation: 'typingDot 1.3s infinite' }}></div>
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: doc.id === 'k' ? 'var(--primary)' : 'var(--secondary)', animation: 'typingDot 1.3s infinite 0.2s' }}></div>
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: doc.id === 'k' ? 'var(--primary)' : 'var(--secondary)', animation: 'typingDot 1.3s infinite 0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="glass-warm border-t border-[var(--border-soft)] px-3 pt-2.5 pb-3">
        <div className="flex gap-2 items-end">
          <textarea
            ref={taRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKey}
            onInput={e => resizeTa(e.target)}
            placeholder="اكتبي ما تشعرين به…"
            rows={1}
            className="flex-1 bg-[var(--surface)] border-2 border-[var(--border-soft)] focus:border-[var(--primary)] rounded-2xl px-4 py-2.5 text-[var(--text)] text-[14px] resize-none outline-none min-h-[46px] max-h-[120px] transition-colors"
          />
          <button
            onClick={() => setShowVoice(true)}
            disabled={loading}
            title="رسالة صوتية"
            aria-label="رسالة صوتية"
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 disabled:opacity-40 shadow-md active:scale-95 transition-transform"
            style={{ background: 'linear-gradient(135deg, #a4d6cc, #5eb2a3)' }}
            data-testid="chat-voice-btn"
          >
            <Mic className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 disabled:opacity-40 shadow-md active:scale-95 transition-transform"
            style={{ background: `linear-gradient(135deg, ${doc.id === 'k' ? '#8b7ff0, #6a5cd8' : '#5eb2a3, #3f8579'})` }}
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>
        <div className="text-center text-[9.5px] mt-1.5" style={{ color: 'var(--text-3)' }}>
          دعم إضافي — لا يُغني عن طبيبك المعالج
        </div>
      </div>

      {/* Voice recorder modal */}
      <VoiceRecorder
        open={showVoice}
        mode="chat"
        patient={patient}
        onClose={() => setShowVoice(false)}
        onResult={(res) => {
          const transcript = res?.transcript || '';
          if (transcript && transcript.trim().length > 0) {
            // Auto-send transcript to current doctor
            setTimeout(() => { setShowVoice(false); sendMessage(transcript); }, 800);
          }
        }}
      />
    </div>
  );
}
